import { IdleQueue } from '../../lib/idlize';
import { Amplitude } from './Amplitude';
import { GTM } from './GTM';
import { TapFiliate } from './Tapfiliate';
import { FacebookPixel } from './FacebookPixel';
import { AnalyticsType, } from '../../types/Analytics';
import { Braze } from './Braze';
import Middleware from '../Middleware';
import { analyticsEmitter } from './AnalyticsEventEmitter';
import { RudderStack } from './RudderStack';
export class Analytics {
    static loadAnalytic(type, { language = 'en' }) {
        switch (type.name) {
            case AnalyticsType.GTM:
                if (!type.config || (type.config && !type.config['GTM_ID'])) {
                    console.error('GTM_ID is missing');
                    break;
                }
                this.AnalyticsQueue.pushTask(() => {
                    GTM.load(type.config, type.callback);
                });
                break;
            case AnalyticsType.AMPLITUDE:
                if (!type.config || (type.config && !type.config['AMPLITUDE_ID'])) {
                    console.error('AMPLITUDE_ID is missing');
                    break;
                }
                this.AnalyticsQueue.pushTask(() => {
                    Amplitude.load({
                        language,
                        config: type.config,
                        callback: type.callback,
                    });
                });
                break;
            case AnalyticsType.BRAZE:
                if (!type.config || (type.config && !type.config['API_KEY'])) {
                    console.error('BRAZE is missing');
                    break;
                }
                this.AnalyticsQueue.pushTask(() => {
                    Braze.load(type.config, type.callback);
                });
                break;
            case AnalyticsType.FBQ:
                if (process.env.NODE_ENV === 'production') {
                    this.AnalyticsQueue.pushTask(() => {
                        FacebookPixel.load(type.callback);
                    });
                }
                break;
            case AnalyticsType.TAPFILIATE:
                if (process.env.NODE_ENV === 'production') {
                    this.AnalyticsQueue.pushTask(() => {
                        TapFiliate.load(type.callback);
                    });
                }
                break;
            case AnalyticsType.RUDDER_STACK:
                if (!type.config ||
                    !type?.config?.['WRITE_KEY'] ||
                    !type?.config?.['DATA_PLANE_URL'] ||
                    !type?.config?.['CONFIG_URL']) {
                    console.error('WRITE_KEY or DATA_PLANE_URL or CONFIG_URL is missing');
                    break;
                }
                RudderStack.load({
                    language,
                    config: type.config,
                    callback: type.callback,
                });
                break;
            default:
                console.error(`Hey! You forgot to load script for ${type}`);
        }
    }
    static init(types, options = this.options) {
        types.forEach(type => {
            this.loadAnalytic(type, options);
        });
        return this;
    }
    static send(type, _data) {
        const data = this.analyticsMiddleware.execute(_data, options => options.type === type);
        switch (type) {
            case AnalyticsType.GTM:
                this.AnalyticsQueue.pushTask(() => GTM.send(data));
                break;
            case AnalyticsType.BRAZE:
                this.AnalyticsQueue.pushTask(() => Braze.sendEvent(data));
                break;
            case AnalyticsType.AMPLITUDE: {
                const event = () => Amplitude.send(data.eventName, this.sanitizeProperties(data.eventProperties, this.eventProperties), this.sanitizeProperties(data.userProperties, this.userProperties), this.sanitizeProperties(data.extraProperties, this.extraProperties));
                if (Amplitude.isReady) {
                    this.AnalyticsQueue.pushTask(event);
                }
                else {
                    analyticsEmitter.on('amplitude', ({ isReady }) => {
                        if (isReady) {
                            this.AnalyticsQueue.pushTask(event);
                        }
                    });
                }
                break;
            }
            case AnalyticsType.FBQ:
                this.AnalyticsQueue.pushTask(() => FacebookPixel.send(data));
                break;
            case AnalyticsType.TAPFILIATE:
                this.AnalyticsQueue.pushTask(() => TapFiliate.send(data));
                break;
            case AnalyticsType.RUDDER_STACK: {
                const event = () => RudderStack.send({
                    eventName: data.eventName,
                    eventData: this.sanitizeProperties(data.eventProperties),
                    userData: this.sanitizeProperties(data.userProperties),
                    extraData: this.sanitizeProperties(data.extraProperties),
                });
                if (RudderStack.ready) {
                    this.AnalyticsQueue.pushTask(event);
                }
                else {
                    analyticsEmitter.on('rudderstack', ({ isReady }) => {
                        if (isReady) {
                            this.AnalyticsQueue.pushTask(event);
                        }
                    });
                }
                break;
            }
            default:
                console.error('Hey! Have you forgot to send analytics report?');
        }
        return Promise.resolve();
    }
    static identify(type) {
        switch (type) {
            case AnalyticsType.RUDDER_STACK:
                if (RudderStack.ready) {
                    this.AnalyticsQueue.pushTask(RudderStack.identify);
                }
                else {
                    analyticsEmitter.on('rudderstack', ({ isReady }) => {
                        if (isReady) {
                            this.AnalyticsQueue.pushTask(RudderStack.identify);
                        }
                    });
                }
                break;
            default:
                console.error('Hey! Have you forgot to implement identify analytics report?');
        }
    }
    static reset(type, resetAnonymousId = false) {
        switch (type) {
            case AnalyticsType.RUDDER_STACK:
                if (RudderStack.ready) {
                    this.AnalyticsQueue.pushTask(() => RudderStack.reset(resetAnonymousId));
                }
                else {
                    analyticsEmitter.on('rudderstack', ({ isReady }) => {
                        if (isReady) {
                            this.AnalyticsQueue.pushTask(() => RudderStack.reset(resetAnonymousId));
                        }
                    });
                }
                break;
            default:
                console.error('Hey! Have you forgot to implement identify analytics report?');
        }
    }
    static updateIndentify(type, userProps) {
        switch (type) {
            case AnalyticsType.RUDDER_STACK:
                if (RudderStack.ready) {
                    this.AnalyticsQueue.pushTask(() => {
                        RudderStack.updateIdentify(userProps);
                    });
                }
                else {
                    analyticsEmitter.on('rudderstack', ({ isReady }) => {
                        if (isReady) {
                            this.AnalyticsQueue.pushTask(() => {
                                RudderStack.updateIdentify(userProps);
                            });
                        }
                    });
                }
                break;
            default:
                console.error('Hey! Have you forgot to implement identify analytics report?');
        }
    }
    static registerMiddleware(type, fn) {
        return this.analyticsMiddleware.register(fn, { type });
    }
    static unregisterMiddleware(id) {
        this.analyticsMiddleware.unregister(id);
    }
    static pushToQueue(fn) {
        this.AnalyticsQueue.pushTask(fn);
    }
    static withEventProperties(partialProperties) {
        this.eventProperties = { ...this.eventProperties, ...partialProperties };
        return this;
    }
    static withUserProperties(partialProperties) {
        this.userProperties = { ...this.userProperties, ...partialProperties };
        return this;
    }
    static withExtraProperties(partialProperties) {
        this.extraProperties = { ...this.extraProperties, ...partialProperties };
        return this;
    }
    static sanitizeProperties(properties = {}, partials = {}) {
        return { ...properties, ...partials };
    }
}
Analytics.eventProperties = {};
Analytics.userProperties = {};
Analytics.extraProperties = {};
Analytics.options = {
    language: 'en',
};
Analytics.analyticsMiddleware = new Middleware();
Analytics.AnalyticsQueue = new IdleQueue({
    defaultMinTaskTime: 20,
    ensureTasksRun: true,
});
